import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import EnviarVendedorModal from '../components/EnviarVendedorModal';
import useTouchKanban from '../hooks/useTouchKanban';

// ── Colunas ────────────────────────────────────────────────────────────────────
const COLS = [
  { key: 'meus_leads',   label: 'Meus Leads',   color: '#60a5fa', desc: 'Cadastrados, sem follow up' },
  { key: 'follow_up',    label: 'Follow Up',    color: '#fb923c', desc: 'Pelo menos um contato registrado' },
  { key: 'com_vendedor', label: 'Com Vendedor', color: '#4ade80', desc: 'Passados para o time de vendas' },
];

const COL_ORDER = ['meus_leads', 'follow_up', 'com_vendedor'];

// ── Helpers ────────────────────────────────────────────────────────────────────
const fmtDate = (d) => {
  if (!d) return '—';
  const str = typeof d === 'string' && !d.includes('T') ? d + 'T12:00:00' : d;
  const date = new Date(str);
  return isNaN(date) ? '—' : date.toLocaleDateString('pt-BR');
};

const fmtDateTime = (d) => {
  if (!d) return '—';
  const date = new Date(d);
  return isNaN(date) ? '—' : date.toLocaleString('pt-BR', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
};

const getColKey = (lead) => {
  if (lead.status === 'aguardando_vendedor') return 'com_vendedor';
  if (lead.followups_count > 0)              return 'follow_up';
  return 'meus_leads';
};

const today = () => new Date().toISOString().slice(0, 10);

// ── Estilos ────────────────────────────────────────────────────────────────────
const S = {
  heading:  { fontSize: 22, fontWeight: 700, color: '#f6c90e', marginBottom: 4 },
  sub:      { fontSize: 13, color: '#475569', marginBottom: 20 },
  board:    { display: 'flex', gap: 14, overflowX: 'auto', paddingBottom: 16, minHeight: 420 },

  col: (c, over) => ({
    minWidth: 230, maxWidth: 270, flex: 1,
    background: over ? `${c}0d` : '#0e1420',
    border: `1px solid ${over ? c + '88' : c + '33'}`,
    borderRadius: 12, padding: 12,
    borderTop: `3px solid ${c}`,
    flexShrink: 0,
    transition: 'background 0.15s, border-color 0.15s',
  }),
  colHead:  { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 },
  colLabel: (c) => ({ fontSize: 11, fontWeight: 700, color: c, textTransform: 'uppercase', letterSpacing: 0.5 }),
  colDesc:  { fontSize: 10, color: '#334155', marginBottom: 12 },
  badge:    (c) => ({ background: `${c}22`, color: c, fontSize: 11, fontWeight: 700, padding: '2px 7px', borderRadius: 10, border: `1px solid ${c}44` }),

  empty: (over) => ({
    textAlign: 'center', fontSize: 12, padding: '28px 0',
    color: over ? '#60a5fa' : '#1e2d45',
    border: over ? '2px dashed #60a5fa44' : '2px dashed transparent',
    borderRadius: 8, transition: 'all 0.15s',
  }),

  card: (isDragging) => ({
    background: isDragging ? 'rgba(246,201,14,0.08)' : '#111827',
    borderRadius: 8, padding: '10px 11px', marginBottom: 8,
    border: `1px solid ${isDragging ? '#f6c90e44' : '#1a2035'}`,
    cursor: 'grab', userSelect: 'none',
    opacity: isDragging ? 0.55 : 1,
    transition: 'opacity 0.15s, border-color 0.15s',
  }),
  cardName: { fontSize: 13, fontWeight: 600, color: '#e2e8f0', marginBottom: 4 },
  cardRow:  { fontSize: 11, color: '#475569', marginBottom: 2 },
  cardFoot: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 4, marginTop: 8 },
  tag:      (c) => ({ background: `${c}22`, color: c, fontSize: 10, fontWeight: 700, padding: '2px 6px', borderRadius: 6, border: `1px solid ${c}33` }),

  btnView:  { background: 'rgba(96,165,250,0.12)', border: '1px solid rgba(96,165,250,0.25)', borderRadius: 5, padding: '2px 8px', cursor: 'pointer', color: '#60a5fa', fontSize: 11, fontWeight: 600, whiteSpace: 'nowrap' },

  // Modal
  overlay:  { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.72)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 },
  modal:    { background: '#0e1420', border: '1px solid #1a2035', borderRadius: 14, padding: 28, width: 400, maxWidth: '92vw' },
  mTitle:   { fontSize: 16, fontWeight: 700, color: '#f6c90e', marginBottom: 3 },
  mSub:     { fontSize: 12, color: '#475569', marginBottom: 20 },
  label:    { fontSize: 12, color: '#94a3b8', marginBottom: 5, display: 'block' },
  input:    { width: '100%', background: '#111827', border: '1px solid #1a2035', borderRadius: 8, padding: '9px 12px', color: '#e2e8f0', fontSize: 13, marginBottom: 14, boxSizing: 'border-box', outline: 'none' },
  textarea: { width: '100%', background: '#111827', border: '1px solid #1a2035', borderRadius: 8, padding: '9px 12px', color: '#e2e8f0', fontSize: 13, marginBottom: 14, boxSizing: 'border-box', minHeight: 88, resize: 'vertical', outline: 'none' },
  hint:     { fontSize: 11, color: '#334155', marginBottom: 14, lineHeight: 1.5 },
  errMsg:   { color: '#f87171', fontSize: 12, marginBottom: 10 },
  btnRow:   { display: 'flex', gap: 10, justifyContent: 'flex-end' },
  btnCancel:  { background: 'transparent', border: '1px solid #1a2035', borderRadius: 8, padding: '8px 18px', color: '#64748b', cursor: 'pointer', fontSize: 13 },
  btnOk:      { background: '#f6c90e', border: 'none', borderRadius: 8, padding: '8px 20px', color: '#080c14', fontWeight: 700, cursor: 'pointer', fontSize: 13 },
};

// ══════════════════════════════════════════════════════════════════════════════
export default function PreVendaKanban() {
  const navigate = useNavigate();
  const [leads, setLeads]     = useState([]);
  const [loading, setLoading] = useState(true);

  // Drag state
  const [dragging, setDragging] = useState(null); // lead being dragged
  const [overCol, setOverCol]   = useState(null);  // col key being hovered
  const dragMoved = useRef(false);                  // evita onClick após drag
  const draggingPvkRef = useRef(null);              // backup ref (evita stale closure no touch)

  // Modal state
  const [modal, setModal]           = useState(null); // { type: 'followup' | 'vendedor', lead }
  const [modalFollowups, setModalFollowups] = useState([]); // follow-ups do lead no modal vendedor
  const [fuForm, setFuForm]         = useState({ data_contato: '', nota: '' });
  const [saving, setSaving]         = useState(false);
  const [errMsg, setErrMsg]         = useState('');

  // ── Carga ──────────────────────────────────────────────────────────────────
  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get('/pre-venda/meus-leads/');
      setLeads(r.data);
    } catch {
      /* silencioso */
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const byCol = (key) => leads.filter(l => getColKey(l) === key);

  // ── Touch Drag (iOS / mobile) ──────────────────────────────────────────────
  const handleDropLogicPvk = (item, targetKey) => {
    const fromKey = getColKey(item);
    if (fromKey === targetKey) return;
    if (COL_ORDER.indexOf(targetKey) < COL_ORDER.indexOf(fromKey)) return; // apenas para frente
    setErrMsg('');
    if (targetKey === 'follow_up') {
      setFuForm({ data_contato: today(), nota: '' });
      setModal({ type: 'followup', lead: item });
    } else if (targetKey === 'com_vendedor') {
      setModalFollowups([]);
      setModal({ type: 'vendedor', lead: item });
      api.get(`/leads/${item.id}/followup-pre-venda/`).then(r => setModalFollowups(r.data)).catch(() => {});
    }
  };

  const {
    boardRef: boardRefPvk,
    touchDragId: touchDragIdPvk,
    touchOverCol: touchOverColPvk,
    onCardTouchStart: onCardTouchStartPvk,
    onBoardTouchEnd: onBoardTouchEndPvk,
    clearTouchOnDragStart: clearTouchPvk,
  } = useTouchKanban(handleDropLogicPvk);

  // ── Drag & Drop ────────────────────────────────────────────────────────────
  const onDragStart = (e, lead) => {
    clearTouchPvk(); // evita conflito no Android Chrome (ambos os eventos disparam)
    draggingPvkRef.current = lead;
    dragMoved.current = false;
    setDragging(lead);
    e.dataTransfer.effectAllowed = 'move';
  };
  const onDragEnd = () => {
    setDragging(null);
    setOverCol(null);
  };
  const onDragOver = (e, colKey) => {
    e.preventDefault();
    dragMoved.current = true;
    e.dataTransfer.dropEffect = 'move';
    setOverCol(colKey);
  };
  const onDragLeave = (e) => {
    // só limpa se saiu de fato da coluna (não entrou num filho)
    if (!e.currentTarget.contains(e.relatedTarget)) setOverCol(null);
  };

  const onDrop = (e, targetKey) => {
    e.preventDefault();
    setOverCol(null);
    const item = draggingPvkRef.current || dragging;
    draggingPvkRef.current = null;
    setDragging(null);
    if (!item) return;
    handleDropLogicPvk(item, targetKey);
  };

  const closeModal = () => { setModal(null); setErrMsg(''); setSaving(false); };

  // ── Submit Follow Up ───────────────────────────────────────────────────────
  const submitFollowUp = async () => {
    if (!fuForm.data_contato) { setErrMsg('Informe a data do contato.'); return; }
    if (!fuForm.nota.trim())  { setErrMsg('Descreva como foi a conversa.'); return; }
    setSaving(true);
    try {
      await api.post(`/leads/${modal.lead.id}/followup-pre-venda/`, fuForm);
      closeModal();
      load();
    } catch {
      setErrMsg('Erro ao salvar. Tente novamente.');
      setSaving(false);
    }
  };

  // ── Render ─────────────────────────────────────────────────────────────────
  if (loading) return <div style={{ color: '#475569', padding: 20 }}>Carregando leads…</div>;

  return (
    <div>
      <div style={S.heading}>Kanban · Pré Venda</div>
      <div style={S.sub}>Arraste os cards para avançar o lead no funil</div>

      <div style={S.board} ref={boardRefPvk} onTouchEnd={onBoardTouchEndPvk}>
        {COLS.map(col => {
          const items = byCol(col.key);
          const isOver = overCol === col.key || touchOverColPvk === col.key;
          return (
            <div
              key={col.key}
              data-col-key={col.key}
              style={S.col(col.color, isOver)}
              onDragOver={e => onDragOver(e, col.key)}
              onDragLeave={onDragLeave}
              onDrop={e => onDrop(e, col.key)}
            >
              {/* Cabeçalho da coluna */}
              <div style={S.colHead}>
                <span style={S.colLabel(col.color)}>{col.label}</span>
                <span style={S.badge(col.color)}>{items.length}</span>
              </div>
              <div style={S.colDesc}>{col.desc}</div>

              {/* Área vazia */}
              {items.length === 0 && (
                <div style={{ ...S.empty(isOver), pointerEvents: 'none' }}>
                  {isOver ? '↓ Solte aqui' : 'Nenhum lead'}
                </div>
              )}

              {/* Cards */}
              {items.map(lead => {
                const isFinal = col.key === 'com_vendedor'; // última coluna — não arrastável
                return (
                <div
                  key={lead.id}
                  draggable={!isFinal}
                  onDragStart={e => !isFinal && onDragStart(e, lead)}
                  onDragEnd={onDragEnd}
                  onTouchStart={e => !isFinal && onCardTouchStartPvk(e, lead)}
                  style={S.card(!isFinal && (dragging?.id === lead.id || touchDragIdPvk === lead.id))}
                >
                  <div style={S.cardName}>{lead.cliente_nome}</div>

                  {lead.cliente_cidade    && <div style={S.cardRow}>📍 {lead.cliente_cidade}</div>}
                  {lead.cliente_telefone  && <div style={S.cardRow}>📞 {lead.cliente_telefone}</div>}
                  {lead.cliente_consumo   && (
                    <div style={S.cardRow}>
                      ⚡ R$ {Number(lead.cliente_consumo).toLocaleString('pt-BR')}/mês
                    </div>
                  )}
                  {col.key === 'com_vendedor' && lead.vendedor_nome && (
                    <div
                      style={{ ...S.cardRow, color: '#4ade80', cursor: lead.cliente_id ? 'pointer' : 'default', textDecoration: lead.cliente_id ? 'underline' : 'none' }}
                      onClick={e => { e.stopPropagation(); lead.cliente_id && navigate(`/jornada/${lead.cliente_id}`); }}
                      onMouseDown={e => e.stopPropagation()}
                    >
                      👤 {lead.vendedor_nome}
                    </div>
                  )}
                  {lead.prazo_resposta_vendedor && (
                    <div style={{ ...S.cardRow, color: '#f6c90e', marginTop: 4 }}>
                      ⏰ Prazo: {fmtDateTime(lead.prazo_resposta_vendedor)}
                    </div>
                  )}

                  <div style={S.cardFoot}>
                    <span style={S.tag(col.color)}>{fmtDate(lead.criado_em)}</span>

                    {lead.followups_count > 0 && (
                      <span style={S.tag('#fb923c')}>
                        {lead.followups_count} follow{lead.followups_count > 1 ? 's' : ''}
                      </span>
                    )}

                    {lead.cliente_id && (
                      <button
                        style={S.btnView}
                        onClick={e => { e.stopPropagation(); navigate(`/jornada/${lead.cliente_id}`); }}
                        onMouseDown={e => e.stopPropagation()}
                      >
                        Ver →
                      </button>
                    )}
                  </div>
                </div>
                );
              })}
            </div>
          );
        })}
      </div>

      {/* ── Modal: Follow Up ──────────────────────────────────────────────── */}
      {modal?.type === 'followup' && (
        <div style={S.overlay} onClick={closeModal}>
          <div style={S.modal} onClick={e => e.stopPropagation()}>
            <div style={S.mTitle}>Registrar Follow Up</div>
            <div style={S.mSub}>{modal.lead.cliente_nome}</div>

            <label style={S.label}>Data do contato</label>
            <input
              type="date"
              style={S.input}
              value={fuForm.data_contato}
              onChange={e => setFuForm(p => ({ ...p, data_contato: e.target.value }))}
            />

            <label style={S.label}>Como foi a conversa?</label>
            <textarea
              style={S.textarea}
              placeholder="Descreva o contato com o cliente…"
              value={fuForm.nota}
              onChange={e => setFuForm(p => ({ ...p, nota: e.target.value }))}
              autoFocus
            />

            {errMsg && <div style={S.errMsg}>{errMsg}</div>}

            <div style={S.btnRow}>
              <button style={S.btnCancel} onClick={closeModal} disabled={saving}>Cancelar</button>
              <button style={S.btnOk}     onClick={submitFollowUp} disabled={saving}>
                {saving ? 'Salvando…' : 'Registrar Follow Up'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Modal: Passar p/ Vendedor ─────────────────────────────────────── */}
      {modal?.type === 'vendedor' && (
        <EnviarVendedorModal
          lead={modal.lead}
          followups={modalFollowups}
          onClose={closeModal}
          onSaved={load}
        />
      )}
    </div>
  );
}
